export function parseEngineOutput(raw, topLevelLead) {
  try {
    var parsed;
    if (typeof raw === 'string') {
      var match = raw.match(/\{[\s\S]*\}/);
      if (match) parsed = JSON.parse(match[0]);
      else throw new Error('No JSON found');
    } else {
      parsed = raw;
    }

    var d1 = parsed.domain_1_commitment || parsed.domain_1 || {};
    var d2 = parsed.domain_2_decision_readiness || parsed.domain_2 || {};
    var d3 = parsed.domain_3_marketing_intelligence || parsed.domain_3 || {};
    var d4 = parsed.domain_4_objections || parsed.domain_4 || {};
    var d5 = parsed.domain_5_emotional || parsed.domain_5 || {};
    var d6 = parsed.domain_6_rep_quality || parsed.domain_6 || {};
    var arch = parsed.archetype || {};
    var meta = parsed.meta_metrics || parsed.meta || {};
    var noshow = parsed.noshow_analysis || {};
    var actions = parsed.recommended_actions || {};
    var conf = parsed.model_confidence || {};

    // V2 stores under participants.customer, V1 stores under customer
    var participants = parsed.participants || {};
    var customer = parsed.customer || participants.customer || {};
    var agent = parsed.agent || participants.agent || {};
    var convo = parsed.conversation || {};
    var meeting = parsed.meeting || parsed.meeting_details || convo.meeting || {};

    // FIX #2: Name fallback chain
    // Priority: V1 customer.name → V2 participants.customer.name → top-level lead.name → top-level lead.lead_name → 'Unknown'
    var leadName = customer.name || (participants.customer && participants.customer.name) || null;
    if (!leadName && topLevelLead) {
      leadName = topLevelLead.name || topLevelLead.lead_name || null;
    }
    if (!leadName) leadName = 'Unknown Lead';

    // Agent name with similar fallback
    var agentName = agent.name || (participants.agent && participants.agent.name) || null;
    if (!agentName && topLevelLead) {
      agentName = topLevelLead.rep_name || null;
    }
    if (!agentName) agentName = 'Unknown Rep';

    // FIX #3: Handle V2 messages array (flat with direction field)
    var inbound = [];
    var outbound = [];

    // V1 format: conversation.inbound_messages and conversation.outbound_messages
    if (convo.inbound_messages && Array.isArray(convo.inbound_messages)) {
      inbound = convo.inbound_messages;
    }
    if (convo.outbound_messages && Array.isArray(convo.outbound_messages)) {
      outbound = convo.outbound_messages;
    }

    // V2 format: flat messages array with direction field (stored in extraction_json)
    // The parsed object might BE the extraction data, or the extraction might be nested
    var messagesArray = parsed.messages || (parsed.extraction && parsed.extraction.messages) || null;
    if (messagesArray && Array.isArray(messagesArray) && inbound.length === 0 && outbound.length === 0) {
      messagesArray.forEach(function(m) {
        var msg = {
          timestamp: m.timestamp || null,
          text: m.text || m.content || '',
          intent: m.intent || [],
          sentiment: m.sentiment || null
        };
        if (m.direction === 'inbound') {
          inbound.push(msg);
        } else if (m.direction === 'outbound') {
          outbound.push(msg);
        }
      });
    }

    // Customer goals (V1 and V2)
    var goals = parsed.customer_goals || convo.customer_goals || [];
    if (goals.length === 0 && customer.goals) goals = customer.goals;
    if (goals.length === 0 && participants.customer && participants.customer.goals) {
      goals = participants.customer.goals;
    }

    // V2 timing & reasoning
    var timing = d1.timing_intelligence || {};
    var reasoning = d1.show_probability_reasoning || {};

    function getBandColor(band) {
      var map = { WEAK_CURIOSITY: '#EF4444', PASSIVE_INTEREST: '#E8A838', ENGAGED: '#38BDF8', COMMITTED: '#4ADE80' };
      return map[band] || '#E8A838';
    }

    return {
      valid: true,
      lead: {
        name: leadName,
        phone: parsed.conversation_key || (topLevelLead && topLevelLead.id) || '',
        rep: agentName,
        team: agent.team || agent.company || '',
        meeting: { date: meeting.date || 'Not set', time: meeting.time || '', type: meeting.type || '' },
        goals: goals,
        inbound_messages: inbound,
        outbound_messages: outbound,
      },
      scores: {
        commitment: { score: d1.lead_commitment_score || 0, band: d1.commitment_band || 'UNKNOWN', color: getBandColor(d1.commitment_band) },
        showUp: d1.show_up_probability || 0,
        ghostRisk: { score: d1.ghost_risk_score || 0, cluster: d1.ghost_risk_cluster || 'UNKNOWN', pattern: d1.ghost_pattern || 'unknown' },
        decisionStage: d2.decision_stage || 'unknown',
        readiness: d2.decision_readiness_score || 0,
        quadrant: d2.readiness_commitment_quadrant || 'unknown',
        perceivedOffer: d3.perceived_offer_type || 'unknown',
        misalignment: d3.marketing_misalignment || false,
        misalignmentReason: d3.misalignment_reason || null,
        misalignmentSeverity: d3.misalignment_severity || 'none',
        expectationAlignment: d3.expectation_alignment_score || 0,
        expectationsDetected: d3.expectations_detected || [],
        objectionSeverity: d4.objection_severity || 0,
        primaryObjection: d4.primary_objection_cluster || 'none_detected',
        hiddenObjectionRisk: d4.hidden_objection_risk || false,
        hiddenObjectionIndicators: d4.hidden_objection_indicators || [],
        objections: d4.objections || [],
        objectionResolution: d4.objection_resolution_status || 'not_applicable',
        emotionalState: d5.emotional_state || 'unknown',
        volatility: d5.emotional_volatility_score || 0,
        confidence: d5.confidence_level || 0,
        trajectory: d5.emotional_trajectory || 'unknown',
        emotionalArc: d5.emotional_arc || {},
        rapportStrength: d6.rapport_strength_score || 0,
        confirmationQuality: d6.confirmation_protocol_quality || 'unknown',
        valueReinforcement: d6.value_reinforcement_score || 0,
        frictionFlag: d6.friction_creation_flag || false,
        frictionEvents: d6.friction_events || [],
        interventions: d6.intervention_recommendation || [],
        interventionUrgency: d6.intervention_urgency || 'not_urgent',
        archetype: arch.primary_archetype || 'unknown',
        archetypeSecondary: arch.secondary_archetype || null,
        archetypeConfidence: arch.archetype_confidence || 0,
        closeProbability: meta.close_probability_estimate || meta.close_probability || 0,
        refundRisk: meta.refund_risk_estimate || meta.refund_risk || 0,
        upsellPotential: meta.upsell_potential_estimate || meta.upsell_potential || 0,
        referralPotential: meta.referral_potential_estimate || meta.referral_potential || 0,
        ltv: meta.lifetime_value_signal || meta.lifetime_value_indicator || 'unknown',
        priority: meta.priority_tier || 'unknown',
        expectedRevenue: meta.expected_revenue || null,
      },
      timing: timing,
      reasoning: reasoning,
      actions: {
        immediate: actions.immediate_actions || [],
        preCall: actions.pre_call_actions || [],
        manager: actions.manager_actions || [],
      },
      noshow: {
        driver: noshow.noshow_risk_driver || 'unknown',
        criticalPoint: noshow.critical_failure_point || '',
        prevention: noshow.prevention_opportunity || '',
      },
      confidence: {
        commitment: conf.commitment_confidence || 0,
        readiness: conf.readiness_confidence || 0,
        marketing: conf.marketing_alignment_confidence || 0,
        objection: conf.objection_confidence || 0,
        emotional: conf.emotional_confidence || 0,
        repQuality: conf.rep_quality_confidence || 0,
        archetype: conf.archetype_confidence || 0,
        meta: conf.meta_prediction_confidence || 0,
      },
      greenFlags: parsed.green_flags || [],
      redFlags: parsed.red_flags || [],
      summary: parsed.executive_summary || 'No summary available.',
      evidenceSummaries: {
        commitment: d1.evidence_summary || '',
        readiness: d2.evidence_summary || '',
        marketing: d3.evidence_summary || '',
        objections: d4.evidence_summary || '',
        emotional: d5.evidence_summary || '',
        repQuality: d6.evidence_summary || '',
      },
      rawJson: parsed,
    };
  } catch (e) {
    return { valid: false, error: e.message };
  }
}
